import { Button, Menu, MenuButton, MenuItem, MenuItems, type MenuItemsProps } from '@headlessui/react';
import { CheckIcon, GlobeAltIcon, LanguageIcon } from '@heroicons/react/24/outline';
import { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Locale } from 'softcat:config/locale.ts';
import { useLocale } from 'softcat:hooks/use-locale.ts';

type LanguageSelectorProperties = {
    anchor?: MenuItemsProps['anchor'];
    showLabel?: boolean;
};

export const LanguageSelector: FunctionComponent<LanguageSelectorProperties> = ({ anchor = 'bottom end', showLabel = false }) => {
    const { locale, updateLanguage } = useLocale();
    const intl = useIntl();

    const languages = [
        {
            locale: Locale.EN,
            name: intl.formatMessage({ id: 'LANGUAGE_SELECTOR_LANGUAGE_ENGLISH' }),
        },
        {
            locale: Locale.DE,
            name: intl.formatMessage({ id: 'LANGUAGE_SELECTOR_LANGUAGE_GERMAN' }),
        },
        {
            locale: Locale.IT,
            name: intl.formatMessage({ id: 'LANGUAGE_SELECTOR_LANGUAGE_ITALIAN' }),
        },
    ];

    return (
        <Menu as="div" className="relative inline-block text-left">
            <div>
                <MenuButton
                    className="inline-flex items-center gap-1 rounded-full bg-white p-2 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    data-testid="language-selector"
                >
                    <GlobeAltIcon className={'size-6 stroke-1 text-gray-600'} />
                    {showLabel && (
                        <div className={'pr-2 text-sm'}>
                            <FormattedMessage id={'LANGUAGE_SELECTOR_SELECT_LANGUAGE'} />
                        </div>
                    )}
                </MenuButton>
            </div>
            <MenuItems
                anchor={anchor}
                className="absolute z-50 mt-2 w-auto origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                transition
            >
                {languages.map((language) => {
                    return (
                        <MenuItem key={language.locale}>
                            <Button
                                className="relative flex w-full items-center gap-x-1.5 py-2 pl-4 pr-8 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                                onClick={() => {
                                    return updateLanguage(language.locale);
                                }}
                            >
                                <LanguageIcon className={'size-4 text-gray-400'} />
                                <div>{language.name}</div>
                                {language.locale === locale && (
                                    <div className={'absolute right-0 px-3 py-2'}>
                                        <CheckIcon className={'size-4 text-indigo-700'} data-testid={`selected-language-${language.locale}`} />
                                    </div>
                                )}
                            </Button>
                        </MenuItem>
                    );
                })}
            </MenuItems>
        </Menu>
    );
};
